import { QueryClient, queryOptions, useMutation, useQueryClient, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { axiosInstance } from '@/api/axios.ts'
import { createQueryKey, FIXED_ASSETS_BASE_KEY } from '@/api/query-keys.ts'
import {
  CreateAssetTypePathParams,
  CreateAssetTypeRequest,
  CreateAssetTypeResponse,
  FixedAssetsTotalResultRequest,
  FixedAssetsTotalsAPI,
  FixedAssetsTotalTable,
  GetAssetTypeEntriesRequest,
  GetAssetTypeEntriesResponse,
  GetFixedAssetsPathParams,
  GetFixedAssetsResponse,
  PostAssetTypeEntriesRequest,
  PostColumnSwitchRequest,
  UpdateAssetTypePathParams,
  UpdateAssetTypeRequest,
  UpdateAssetTypeResponse,
  UpdateFixedAssetsSettingsPathParams,
  UpdateFixedAssetsSettingsRequest,
  UpdateFixedAssetsSettingsResponse
} from './types.ts'
import { PeriodTypeView } from '@/types'
import { DataViewType } from '@/feature/financial-model/ecommerce/financial-tables/api/types.ts'
import { usePageContext } from '@/lib/context/page'
import { FIXED_ASSETS_TOTAL_RESULTS_MOCK } from '@/feature/financial-model/ecommerce/fixed-assets/api/mocks.tsx'

// Query keys

export const FIXED_ASSETS_API_QUERY_KEYS = {
  fixedAssets: `fixedAssets`,
  fixedAssetsSettings: `fixedAssetsSettings`,
  assetType: `assetType`,
  assetTypeEntries: `assetTypeEntries`,
  assetTypeEntriesColumnConfig: `assetTypeEntriesColumnConfig`,
  totalResults: `fixedAssetsTotalResults`
}

// **** GET -> FIXED ASSETS DATA **** //
const createFixedAssetsQueryKey = ({ financialModelId }: GetFixedAssetsPathParams) =>
  createQueryKey({
    baseKey: FIXED_ASSETS_BASE_KEY,
    keys: [FIXED_ASSETS_API_QUERY_KEYS.fixedAssets, financialModelId]
  })

export const getFixedAssets = async ({ financialModelId }: GetFixedAssetsPathParams): Promise<GetFixedAssetsResponse> =>
  (await axiosInstance.get<GetFixedAssetsResponse>(`/financial-models/${financialModelId}/fixed-assets/`)).data

export const useGetFixedAssets = ({ financialModelId }: GetFixedAssetsPathParams) =>
  useSuspenseQuery(
    queryOptions({
      queryKey: createFixedAssetsQueryKey({ financialModelId }),
      queryFn: () => getFixedAssets({ financialModelId })
    })
  )

// **** PUT -> FIXED ASSETS SETTINGS **** //
export const updateFixedAssetsSettings = async ({
  financialModelId,
  modelling_approach
}: UpdateFixedAssetsSettingsPathParams & UpdateFixedAssetsSettingsRequest): Promise<UpdateFixedAssetsSettingsResponse> =>
  (await axiosInstance.put<UpdateFixedAssetsSettingsResponse>(`/financial-models/${financialModelId}/fixed-assets/settings`, { modelling_approach }))
    .data

export const useUpdateFixedAssetsSettings = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ financialModelId, modelling_approach }: UpdateFixedAssetsSettingsPathParams & UpdateFixedAssetsSettingsRequest) =>
      updateFixedAssetsSettings({
        financialModelId,
        modelling_approach
      }),
    onSuccess: (_, { financialModelId }) => {
      queryClient.invalidateQueries({
        queryKey: createFixedAssetsQueryKey({ financialModelId })
      })
    }
  })
}

// **** POST -> ASSET TYPE **** //
export const createAssetType = async ({
  financialModelId,
  ...requestData
}: CreateAssetTypePathParams & CreateAssetTypeRequest): Promise<CreateAssetTypeResponse> =>
  (await axiosInstance.post<CreateAssetTypeResponse>(`/financial-models/${financialModelId}/fixed-assets/type`, requestData)).data

export const useCreateAssetType = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ financialModelId, ...requestData }: CreateAssetTypePathParams & CreateAssetTypeRequest) =>
      createAssetType({
        financialModelId,
        ...requestData
      }),
    onSuccess: (_, { financialModelId }) => {
      queryClient.invalidateQueries({
        queryKey: createFixedAssetsQueryKey({ financialModelId })
      })
    }
  })
}

// **** PUT -> ASSET TYPE **** //
export const updateAssetType = async ({
  financialModelId,
  ...requestData
}: UpdateAssetTypePathParams & UpdateAssetTypeRequest): Promise<UpdateAssetTypeResponse> =>
  (await axiosInstance.put<UpdateAssetTypeResponse>(`/financial-models/${financialModelId}/fixed-assets/type`, requestData)).data

export const useUpdateAssetType = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ financialModelId, ...requestData }: UpdateAssetTypePathParams & UpdateAssetTypeRequest) =>
      updateAssetType({
        financialModelId,
        ...requestData
      }),
    onSuccess: (_, { financialModelId }) => {
      queryClient.invalidateQueries({
        queryKey: createFixedAssetsQueryKey({ financialModelId })
      })
    }
  })
}

// **** GET -> FIXED ASSETS TYPE ENTRIES **** //

export const createAssetTypeEntriesQueryKey = ({
  financialModelId,
  assetTypeId,
  period,
  view
}: {
  financialModelId: string
  assetTypeId: string
  period: string
  view: DataViewType
}) =>
  createQueryKey({
    baseKey: FIXED_ASSETS_BASE_KEY,
    keys: [FIXED_ASSETS_API_QUERY_KEYS.assetTypeEntries, financialModelId, assetTypeId, period, view]
  })

const getAssetTypeEntries = async ({ financialModelId, assetTypeId, period, view }: GetAssetTypeEntriesRequest) => {
  const response = await axiosInstance.get<GetAssetTypeEntriesResponse>(`/financial-models/${financialModelId}/fixed-assets/entries/${assetTypeId}`, {
    params: {
      financial_model_id: financialModelId,
      period_type: period,
      view
    }
  })
  return response.data
}

export const useGetAssetTypeEntries = ({ financialModelId, assetTypeId, period, view }: GetAssetTypeEntriesRequest) =>
  useSuspenseQuery(
    queryOptions({
      queryKey: createAssetTypeEntriesQueryKey({
        financialModelId,
        assetTypeId,
        period,
        view
      }),
      queryFn: () => getAssetTypeEntries({ financialModelId, assetTypeId, period, view })
    })
  )

export const useGetAssetTypeEntriesQuery = ({ financialModelId, assetTypeId, period, view }: GetAssetTypeEntriesRequest) =>
  useQuery({
    queryKey: createAssetTypeEntriesQueryKey({ financialModelId, assetTypeId, period, view }),
    queryFn: () => getAssetTypeEntries({ financialModelId, assetTypeId, period, view }),
    enabled: Boolean(financialModelId && assetTypeId)
  })
// **** POST -> FIXED ASSETS TYPE ENTRIES **** //

const postFixedAssetEntries = async ({ financialModelId, assetTypeId: asset_type_id, period, config, ...body }: PostAssetTypeEntriesRequest) => {
  const response = await axiosInstance.post<unknown>(`/financial-models/${financialModelId}/fixed-assets/entries`, {
    ...body,
    asset_type_id,
    config: period === `historical` ? null : config
  })
  return response.data
}
export const createPostFixedAssetEntriesMutationArgs = (queryClient: QueryClient) => ({
  mutationFn: ({ financialModelId, assetTypeId, period, view, ...body }: PostAssetTypeEntriesRequest) =>
    postFixedAssetEntries({
      financialModelId,
      assetTypeId,
      period,
      view,
      ...body
    }),
  onSuccess: (_: unknown, { financialModelId, assetTypeId, period, view }: PostAssetTypeEntriesRequest) => {
    queryClient.invalidateQueries({
      queryKey: createAssetTypeEntriesQueryKey({
        financialModelId,
        assetTypeId,
        period,
        view
      })
    })
  }
})

export const usePostFixedAssetEntries = () => {
  const queryClient = useQueryClient()
  return useMutation(createPostFixedAssetEntriesMutationArgs(queryClient))
}

export const createBulkUpdateFixedAssetEntriesMutationArgs = () => ({
  mutationFn: ({ financialModelId, assetTypeId, period, view, ...body }: PostAssetTypeEntriesRequest) =>
    postFixedAssetEntries({
      financialModelId,
      assetTypeId,
      period,
      view,
      ...body
    })
})

export const useBulkUpdateFixedAssetEntries = () => useMutation(createBulkUpdateFixedAssetEntriesMutationArgs())

// **** POST -> SWITCH COLUMN TYPE **** //

const switchFixedAssetsColumnType = async (params: PostColumnSwitchRequest) => {
  const { financial_model_id, column_name, ...rest } = params
  const response = await axiosInstance.post<unknown>(`/financial-models/${financial_model_id}/fixed-assets/config/${column_name}`, undefined, {
    params: rest
  })
  return response.data
}

export const useSwitchFixedAssetsColumnType = () => {
  const { state } = usePageContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: PostColumnSwitchRequest) => switchFixedAssetsColumnType(params),
    onSuccess: (_: unknown, { financial_model_id, asset_type_id }: PostColumnSwitchRequest) => {
      queryClient.invalidateQueries({
        queryKey: createAssetTypeEntriesQueryKey({
          financialModelId: financial_model_id,
          assetTypeId: asset_type_id,
          period: state.queryPeriod,
          view: state.view
        })
      })
    }
  })
}

// **** GET -> TOTALS DATA **** //

const getFixedAssetsTotalResults = async <P extends PeriodTypeView = PeriodTypeView>(
  params: FixedAssetsTotalResultRequest<P>
): Promise<FixedAssetsTotalsAPI<P>> => {
  const response = await axiosInstance.get<FixedAssetsTotalsAPI<P>>(`financial-models/${params.financial_model_id}/fixed-assets/totals/`, {
    params: { view: params.view, type: `all` }
  })
  return response.data
}

const createFixedAssetsTotalResultsQueryKey = (params: FixedAssetsTotalResultRequest) =>
  createQueryKey({
    baseKey: FIXED_ASSETS_BASE_KEY,
    keys: [FIXED_ASSETS_API_QUERY_KEYS.totalResults, params.financial_model_id, params.view]
  })

export const useGetFixedAssetsTotalResults = <P extends PeriodTypeView = PeriodTypeView>(params: FixedAssetsTotalResultRequest<P>) =>
  useSuspenseQuery({
    queryFn: () => getFixedAssetsTotalResults<P>(params),
    queryKey: createFixedAssetsTotalResultsQueryKey(params),
    select: data => {
      const intangibleAssetsApi = data.categories.find(c => c.category === `intangible`)
      const tangibleAssetsApi = data.categories.find(c => c.category === `tangible`)

      const financialAssetsApi = data.categories.find(c => c.category === `financial`)
      if (!intangibleAssetsApi || !tangibleAssetsApi || !financialAssetsApi) {
        throw new Error(`fixed assets totals > invalid api data`)
      }

      const isYearlyView = params.view === `yearly`
      return {
        available_tables: FIXED_ASSETS_TOTAL_RESULTS_MOCK.available_tables,
        total_fixed_assets: {
          ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.total_fixed_assets,
          head: {
            ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.total_fixed_assets.head,
            is_yearly_view: isYearlyView,
            periods: data.total.map(p => ({
              ...p,
              is_cxgr: false,
              values: {
                value: { value: 0, type: `blank` }
              }
            }))
          },
          gross_book_value: {
            ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.total_fixed_assets.gross_book_value,
            is_yearly_view: isYearlyView,
            periods: data.total.map(p => ({
              ...p,
              is_cxgr: false,
              values: {
                value: { value: p.gross_book_value, type: `currency` }
              }
            }))
          },
          fair_value_adjustments: {
            ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.total_fixed_assets.fair_value_adjustments,
            is_yearly_view: isYearlyView,
            periods: data.total.map(p => ({
              ...p,
              is_cxgr: false,
              values: {
                value: { value: p.accumulated_a_and_d, type: `absolute` }
              }
            }))
          },
          net_book_value: {
            ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.total_fixed_assets.net_book_value,
            is_yearly_view: isYearlyView,
            periods: data.total.map(p => ({
              ...p,
              is_cxgr: false,
              values: {
                value: { value: p.net_book_value, type: `currency` }
              }
            }))
          }
        },
        intangible_assets: {
          ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.intangible_assets,
          head: {
            ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.intangible_assets.head,
            is_yearly_view: isYearlyView,
            periods: data.total.map(p => ({
              ...p,
              is_cxgr: false,
              values: {
                value: { value: 0, type: `blank` }
              }
            }))
          },
          gross_book_value: {
            ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.intangible_assets.gross_book_value,
            is_yearly_view: isYearlyView,
            periods: intangibleAssetsApi.periods.map(p => ({
              ...p,
              is_cxgr: false,
              values: {
                value: { value: p.gross_book_value, type: `currency` }
              }
            }))
          },
          a_and_d_rate: {
            ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.intangible_assets.a_and_d_rate,
            is_yearly_view: isYearlyView,
            periods: intangibleAssetsApi.periods.map(p => ({
              ...p,
              is_cxgr: false,
              values: {
                value: { value: p.accumulated_a_and_d, type: `absolute` }
              }
            }))
          },
          net_book_value: {
            ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.intangible_assets.net_book_value,
            is_yearly_view: isYearlyView,
            periods: intangibleAssetsApi.periods.map(p => ({
              ...p,
              is_cxgr: false,
              values: {
                value: { value: p.net_book_value, type: `currency` }
              }
            }))
          }
        },
        tangible_assets: {
          ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.tangible_assets,
          head: {
            ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.tangible_assets.head,
            is_yearly_view: isYearlyView,
            periods: data.total.map(p => ({
              ...p,
              is_cxgr: false,
              values: {
                value: { value: 0, type: `blank` }
              }
            }))
          },
          gross_book_value: {
            ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.tangible_assets.gross_book_value,
            is_yearly_view: isYearlyView,
            periods: tangibleAssetsApi.periods.map(p => ({
              ...p,
              is_cxgr: false,
              values: {
                value: { value: p.gross_book_value, type: `currency` }
              }
            }))
          },
          a_and_d_rate: {
            ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.tangible_assets.a_and_d_rate,
            is_yearly_view: isYearlyView,
            periods: tangibleAssetsApi.periods.map(p => ({
              ...p,
              is_cxgr: false,
              values: {
                value: { value: p.accumulated_a_and_d, type: `absolute` }
              }
            }))
          },
          net_book_value: {
            ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.tangible_assets.net_book_value,
            is_yearly_view: isYearlyView,
            periods: tangibleAssetsApi.periods.map(p => ({
              ...p,
              is_cxgr: false,
              values: {
                value: { value: p.net_book_value, type: `currency` }
              }
            }))
          }
        },
        financial_assets: {
          ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.financial_assets,
          head: {
            ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.financial_assets.head,
            is_yearly_view: isYearlyView,
            periods: data.total.map(p => ({
              ...p,
              is_cxgr: false,
              values: {
                value: { value: 0, type: `blank` }
              }
            }))
          },
          gross_book_value: {
            ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.financial_assets.gross_book_value,
            is_yearly_view: isYearlyView,
            periods: financialAssetsApi.periods.map(p => ({
              ...p,
              is_cxgr: false,
              values: {
                value: { value: p.gross_book_value, type: `currency` }
              }
            }))
          },
          fair_value_adjustments: {
            ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.financial_assets.fair_value_adjustments,
            is_yearly_view: isYearlyView,
            periods: financialAssetsApi.periods.map(p => ({
              ...p,
              is_cxgr: false,
              values: {
                value: { value: p.accumulated_a_and_d, type: `absolute` }
              }
            }))
          },
          net_book_value: {
            ...FIXED_ASSETS_TOTAL_RESULTS_MOCK.financial_assets.net_book_value,
            is_yearly_view: isYearlyView,
            periods: financialAssetsApi.periods.map(p => ({
              ...p,
              is_cxgr: false,
              values: {
                value: {
                  value: p.net_book_value,
                  type: `currency`
                }
              }
            }))
          }
        }
      } as unknown as FixedAssetsTotalTable<P>
    }
  })
